// SharedSkillsList — super_admin /dashboard/platform/shared-skills 首頁。
// 列出 active + deprecated 共用技能；可篩選 + 跳轉到 detail。
// Spec docs/specs/shared-skill-layer.md §9.

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, Inbox, AlertCircle, ListChecks } from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import {
  listActiveSkills, friendlySharedSkillsError,
  DOMAIN_LABEL, RISK_LABEL, ACTIVE_STATUS_LABEL,
  type ActiveSkillListItem, type SkillDomain, type SkillRiskLevel, type ActiveSkillStatus,
} from "../../lib/sharedSkillsApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type State =
  | { status: "loading" }
  | { status: "ready"; items: ActiveSkillListItem[]; total: number }
  | { status: "error"; message: string };

const ALL_DOMAINS: SkillDomain[] = [
  "route_setup", "task_setup", "task_update", "notification_rule",
  "report_generation", "incident_handling", "onboarding",
  "multi_site_rollout", "roster_planning", "general",
];
const ALL_RISKS: SkillRiskLevel[] = ["low", "medium", "high", "critical"];
const ALL_STATUSES: ActiveSkillStatus[] = ["active", "deprecated"];

const STATUS_BADGE: Record<ActiveSkillStatus, string> = {
  active:     "inline-flex items-center rounded-md bg-emerald-500/15 text-emerald-300 px-2 py-0.5 text-2xs",
  deprecated: "inline-flex items-center rounded-md bg-zinc-500/15 text-zinc-300 px-2 py-0.5 text-2xs",
  rejected:   "inline-flex items-center rounded-md bg-red-500/15 text-red-300 px-2 py-0.5 text-2xs",
};

const RISK_BADGE: Record<SkillRiskLevel, string> = {
  low:      "text-emerald-300",
  medium:   "text-amber-300",
  high:     "text-orange-300",
  critical: "text-red-300",
};

export default function SharedSkillsList(): React.ReactElement {
  const [state, setState] = useState<State>({ status: "loading" });
  const [statusFilter, setStatusFilter] = useState<ActiveSkillStatus[]>(["active"]);
  const [domainFilter, setDomainFilter] = useState<SkillDomain | "">("");
  const [riskFilter, setRiskFilter] = useState<SkillRiskLevel | "">("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listActiveSkills({
        status: statusFilter,
        domain: domainFilter || undefined,
        risk_level: riskFilter || undefined,
        q: q.trim() || undefined,
        limit: 100,
      });
      setState({ status: "ready", items: r.items, total: r.total });
    } catch (e) {
      setState({ status: "error", message: friendlySharedSkillsError(e) });
    }
  }, [statusFilter, domainFilter, riskFilter, q]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="共用技能庫"
        description="跨租戶可重用的 AI 操作知識。經 super_admin 審核後注入 AI runtime。"
        actions={
          <Link
            to="/dashboard/platform/shared-skills/candidates"
            className="inline-flex items-center gap-1.5 rounded-md bg-accent/15 hover:bg-accent/25 px-3 py-1.5 text-xs text-accent transition-colors"
          >
            <ListChecks className="h-3.5 w-3.5" />
            候選審核佇列
          </Link>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-bg-surface p-3">
        <select
          value={statusFilter.join(",")}
          onChange={(e) => setStatusFilter(e.target.value.split(",").filter(Boolean) as ActiveSkillStatus[])}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="active">啟用中</option>
          <option value="deprecated">已停用</option>
          <option value="active,deprecated">全部</option>
        </select>
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value as SkillDomain | "")}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="">全部 domain</option>
          {ALL_DOMAINS.map((d) => <option key={d} value={d}>{DOMAIN_LABEL[d]}</option>)}
        </select>
        <select
          value={riskFilter}
          onChange={(e) => setRiskFilter(e.target.value as SkillRiskLevel | "")}
          className="rounded-md bg-bg-inset border border-border text-xs px-2 py-1.5"
        >
          <option value="">全部風險</option>
          {ALL_RISKS.map((r) => <option key={r} value={r}>{RISK_LABEL[r]}</option>)}
        </select>
        <input
          type="search"
          placeholder="搜尋標題 / 摘要..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[180px] rounded-md bg-bg-inset border border-border text-xs px-3 py-1.5"
        />
      </div>

      {/* Body */}
      {state.status === "loading" && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
        </div>
      )}
      {state.status === "error" && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {state.message}
        </div>
      )}
      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState
          icon={Inbox}
          title="目前沒有共用技能"
          description="待 AI 提出候選並經 super_admin 審核批准後，這裡會列出 active 技能。"
        />
      )}
      {state.status === "ready" && state.items.length > 0 && (
        <div className="space-y-2">
          <div className="text-2xs text-text-muted px-1">共 {state.total} 條（顯示前 {state.items.length} 條）</div>
          {state.items.map((s) => (
            <Link
              key={s.id}
              to={`/dashboard/platform/shared-skills/${s.id}`}
              className="block rounded-lg border border-border bg-bg-surface hover:bg-bg-inset transition-colors p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-primary truncate">{s.title}</span>
                    <span className={STATUS_BADGE[s.status]}>{ACTIVE_STATUS_LABEL[s.status]}</span>
                    <span className="text-2xs text-text-muted font-mono">v{s.version}</span>
                  </div>
                  <p className="mt-1 text-xs text-text-secondary line-clamp-2">{s.summary}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-text-muted">
                    <span>domain: {DOMAIN_LABEL[s.domain]}</span>
                    <span className={RISK_BADGE[s.risk_level]}>風險: {RISK_LABEL[s.risk_level]}</span>
                    <span>priority: {s.priority}</span>
                    <span className="font-mono">{s.public_id}</span>
                    <span>批准於 {formatTaipeiDateTime(s.approved_at)}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
